import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import { useAlert } from '../../context/AlertContext';
import ProgressBar from '../../components/ProgressBar';
import styles from './LoginPage.module.css';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function LoginPage({ onSwitch }) {
  const [formData, setFormData] = useState({
    emailOrMobile: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [fadeout, setFadeout] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { showProgressBar, updateProgressBar, hideProgressBar, progress, isVisible } = useProgressBar();

  const { emailOrMobile, password } = formData;

  const onChange = useCallback((e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value })), []);

  const validate = useCallback(() => {
    let tempErrors = {};
    if (!emailOrMobile) {
      tempErrors.emailOrMobile = 'Email or mobile number is required.';
    } else if (!emailOrMobile.match(/^(\S+@\S+\.\S+)|(^[0-9]{10}$)/)) {
      tempErrors.emailOrMobile = 'Please enter a valid email or 10-digit mobile number.';
    }
    if (!password) tempErrors.password = 'Password is required.';
    
    setErrors(tempErrors);
    setFadeout(false);
    setTimeout(() => setFadeout(true), 2000);
    return Object.keys(tempErrors).length === 0;
  }, [emailOrMobile, password]);

  const handleNavigation = useCallback((user) => {
    if (user.kycStatus === 'not submitted') {
      navigate('/kyc');
    } else if (user.additionalDetailsStatus === 'not submitted') {
      navigate('/additional-details');
    } else if (user.bankStatus === 'not submitted') {
      navigate('/bank-details');
    } else if (user.kycStatus === 'pending' || user.bankStatus === 'pending' || user.kycStatus === 'progress' || user.bankStatus === 'progress') {
      navigate('/account-status');
    } else {
      navigate('/');
    }
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      showProgressBar();
      updateProgressBar(30);

      try {
        await new Promise(resolve => setTimeout(resolve, 250));
        updateProgressBar(60);

        const { user } = await login(emailOrMobile, password);

        updateProgressBar(90);
        await new Promise(resolve => setTimeout(resolve, 250));
        
        showAlert('Login successful!', 'success');
        handleNavigation(user);
      } catch (err) {
        const errorMsg = err.response?.data?.msg || 'An unexpected error occurred. Please try again.';
        showAlert(`Login failed: ${errorMsg}`, 'error');
      } finally {
        hideProgressBar();
      }
    }
  };

  const togglePassword = useCallback(() => setShowPassword(!showPassword), [showPassword]);

  return (
    <div className={styles['login-page-wrapper']}>
      <ProgressBar progress={progress} isVisible={isVisible} />
      <div className={styles['login-form']}>
        <h2>Welcome Back</h2>
        <div className={styles['form-intro']}>
          <p>Login to your account to continue</p>
        </div>
        <form onSubmit={onSubmit}>
          <div className={styles['form-group']}>
            <label htmlFor="emailOrMobile">Email/Mobile</label>
            <input
              type="text"
              id="emailOrMobile"
              name="emailOrMobile"
              value={emailOrMobile}
              onChange={onChange}
              placeholder="Enter your email or mobile number"
            />
            {errors.emailOrMobile && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.emailOrMobile}</p>}
          </div>
          <div className={styles['form-group']}>
            <label htmlFor="password">Password</label>
            <div className={styles['input-wrapper']}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={onChange}
                placeholder="Enter your password"
              />
              <span onClick={togglePassword} className={styles['eye-icon']}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {errors.password && <p className={`error-text ${fadeout ? "fade-out" : ""}`}>{errors.password}</p>}
          </div>
          <button type="submit" className={styles['btn-login']}>Login</button>
        </form>
        <div className={styles['form-footer']}>
          <div className={styles['or-divider']}><span>OR</span></div>
          <p>
            Don't have an account? {' '}
            {onSwitch ? (
              <span className={styles.switchLink} onClick={onSwitch}>Register</span>
            ) : (
              <Link to="/landing" state={{ openRegister: true }}>Register</Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
